import { resolveThermalPrintableWidthMm } from "./thermalPrintLayout";
import { groupPrintedItems, groupPrintedReserveLines } from "../shared/printedItems";

const toMoney = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatMoney = (value) =>
  String(Math.round(toMoney(value))).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

const computeExtrasTotal = (extras) =>
  (Array.isArray(extras) ? extras : []).reduce((sum, extra) => {
    if (extra?.type === "accompaniment") return sum;
    const explicit = toMoney(extra?.total_price ?? extra?.totalPrice);
    if (explicit > 0) return sum + explicit;
    const quantity = toMoney(extra?.quantity ?? extra?.selectedQuantity ?? extra?.qty) || 1;
    const price = toMoney(extra?.price ?? extra?.unit_price ?? extra?.unitPrice);
    return sum + price * quantity;
  }, 0);

const computeThermalLineTotal = (item) => {
  const rawExplicit = item?.total_price_with_extras ?? item?.totalPriceWithExtras;
  if (rawExplicit != null && Number.isFinite(Number(rawExplicit))) return Number(rawExplicit);

  const quantity = toMoney(item?.quantity ?? item?.qty) || 1;
  const unitPrice = toMoney(
    item?.unit_price ?? item?.unitPrice ?? item?.price ?? item?.product_price,
  );
  const explicitBase = toMoney(item?.total_price ?? item?.totalPrice);
  const extrasTotal = toMoney(item?.extras_total ?? item?.extrasTotal) ||
    computeExtrasTotal(item?.extras);

  // Les lignes persistées portent déjà leur total de base et leurs extras totaux.
  // Les brouillons, eux, portent des prix unitaires : leurs extras suivent la quantité.
  if (explicitBase > 0) return explicitBase + extrasTotal;
  return (unitPrice + extrasTotal) * quantity;
};

const normalizePaperWidth = (value) => (Number(value) === 58 ? 58 : 80);

const renderExtras = (extras) => {
  const rows = (Array.isArray(extras) ? extras : [])
    .map((extra) => {
      const quantity = toMoney(extra?.quantity ?? extra?.selectedQuantity ?? extra?.qty) || 1;
      const isAccompaniment = extra?.type === "accompaniment";
      const total = isAccompaniment ? 0 : toMoney(extra?.total_price ?? extra?.totalPrice) ||
        toMoney(extra?.price ?? extra?.unit_price ?? extra?.unitPrice) * quantity;
      const name = extra?.name || extra?.extra_name || extra?.extraName || "Extra";
      const prefix = isAccompaniment ? "•" : "+";
      return `<div class="extra">${prefix} ${escapeHtml(name)}${quantity > 1 ? ` ×${quantity}` : ""}${total ? ` · ${formatMoney(total)}` : ""}</div>`;
    });
  return rows.join("");
};

const fromMinor = (value) => toMoney(value) / 100;
const reservePublicReference = (proof) =>
  proof?.publicReference ||
  (proof?.mode === "issue" ? proof?.paymentNumbers?.[0] : "") ||
  proof?.reserveId ||
  "";

const renderReserve = (proof) => {
  if (!proof || !["issue", "redeem"].includes(proof.mode)) return "";

  const isIssue = proof.mode === "issue";
  const lines = isIssue ? groupPrintedReserveLines(proof.lines) : [];
  return `
    <div class="rule"></div>
    <section class="reserve">
      <div class="section-title">${isIssue ? "ARTICLES EN RÉSERVE" : "RÉSERVE UTILISÉE"}</div>
      ${lines.map((line) => `
        <div class="item reserve-item">
          <div class="item-main">
            <div class="item-name">${toMoney(line?.quantity) || 1}× ${escapeHtml(line?.label || "Article")}</div>
            <div class="money">${formatMoney(fromMinor(line?.netMinor))}</div>
          </div>
        </div>
      `).join("")}
      <div class="row reserve-total"><span>${isIssue ? "Valeur mise en réserve" : "Montant déjà payé utilisé"}</span><span class="money">${formatMoney(fromMinor(proof.reserveMinor))}</span></div>
      <div class="row"><span>${isIssue ? "Montant encaissé" : "Complément encaissé"}</span><span class="money">${formatMoney(fromMinor(proof.externalMinor))}</span></div>
      <div class="row"><span>Solde disponible</span><span class="money">${formatMoney(fromMinor(proof.balanceMinor))}</span></div>
      <div class="reserve-reference">Référence : ${escapeHtml(reservePublicReference(proof))}</div>
    </section>`;
};

const renderUsbTicketHtml = ({ kind = "receipt", payload = {}, paperWidthMm = 80 }) => {
  const rollWidth = normalizePaperWidth(paperWidthMm);
  const width = resolveThermalPrintableWidthMm(rollWidth);
  const isReceipt = kind === "receipt";
  const items = groupPrintedItems(payload.items, { getLineTotal: computeThermalLineTotal });
  const discount = Math.max(0, toMoney(payload.discount));
  const timestamp = new Date(payload.timestamp || Date.now());
  const dateLabel = Number.isNaN(timestamp.getTime())
    ? ""
    : timestamp.toLocaleString("fr-FR").replace(/\u202F|\u00A0/g, " ");
  const number = payload.payment_number || payload.invoice_number || payload.order_number || "";
  const title = isReceipt ? "REÇU" : "FACTURE";
  // Les imprimantes POS 80 mm ont souvent une zone non imprimable plus large
  // a droite. Decaler le contenu vers la gauche conserve la largeur utile tout
  // en protegeant les montants alignes a droite du rognage par le pilote.
  const leftMargin = 2.5;
  const rightMargin = rollWidth === 58 ? 2.5 : 5.5;
  const itemFont = rollWidth === 58 ? 11.5 : 12.5;
  const itemAmountFont = rollWidth === 58 ? 12.5 : 15;
  const totalFont = rollWidth === 58 ? 17 : 19;
  const methods = Array.isArray(payload.payment_methods) ? payload.payment_methods : [];
  const isCreditMethod = (value) => {
    const normalized = String(value || "").toLowerCase();
    return normalized.includes("crédit") || normalized.includes("credit");
  };

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>${title}</title>
  <style>
    @page { margin: 0 !important; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { margin: 0; padding: 0; width: ${width}mm; max-width: ${width}mm; background: #fff; color: #000; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; font-weight: 600; }
    .ticket { width: 100%; margin: 0 !important; padding: 0 ${rightMargin}mm 4mm ${leftMargin}mm; overflow: hidden; }
    .center { text-align: center; }
    .brand { font-size: ${rollWidth === 58 ? 17 : 19}px; font-weight: 900; line-height: 1.15; overflow-wrap: anywhere; }
    .contact { margin-top: 1mm; font-size: 10px; line-height: 1.3; overflow-wrap: anywhere; }
    .rule { border-top: 1px dashed #000; margin: 2.5mm 0; }
    .document-title { font-size: 14px; font-weight: 900; letter-spacing: .12em; }
    .meta { margin-top: 1.5mm; line-height: 1.45; }
    .meta-row { display: flex; gap: 2mm; }
    .meta-label { flex: 0 0 auto; }
    .meta-value { min-width: 0; overflow-wrap: anywhere; font-weight: 800; }
    .section-title { margin-bottom: 1.5mm; font-size: 10px; font-weight: 900; letter-spacing: .12em; }
    .item { padding: 1.8mm 0; border-bottom: 1px dotted #777; page-break-inside: avoid; break-inside: avoid; }
    .item:last-child { border-bottom: 0; }
    .item-main { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 2mm; align-items: start; font-size: ${itemFont}px; }
    .item-name { min-width: 0; font-weight: 900; line-height: 1.25; overflow-wrap: anywhere; }
    .item-main .money { font-size: ${itemAmountFont}px; line-height: 1.1; }
    .money { text-align: right; white-space: nowrap; font-family: "Courier New", monospace; font-weight: 900; font-variant-numeric: tabular-nums; }
    .extra { margin-top: .8mm; padding-left: 3mm; font-size: 9.5px; line-height: 1.25; overflow-wrap: anywhere; }
    .totals { margin-top: 1mm; }
    .row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 2mm; margin: 1mm 0; align-items: baseline; }
    .total { margin-top: 2mm; padding-top: 2mm; border-top: 2px solid #000; font-size: ${totalFont}px; font-weight: 900; }
    .payment-title { margin-top: 3mm; }
    .reserve-total { margin-top: 2mm; font-weight: 900; }
    .reserve-reference { margin-top: 2mm; font-size: 9px; line-height: 1.3; overflow-wrap: anywhere; }
    .footer { margin-top: 4mm; text-align: center; font-size: 9px; line-height: 1.4; }
  </style>
</head>
<body>
  <main class="ticket">
    <header class="center">
      <div class="brand">${escapeHtml(payload.restaurant_name || "Restaurant")}</div>
      ${payload.restaurant_address ? `<div class="contact">${escapeHtml(payload.restaurant_address)}</div>` : ""}
      ${payload.restaurant_phone ? `<div class="contact">Tél. ${escapeHtml(payload.restaurant_phone)}</div>` : ""}
    </header>
    <div class="rule"></div>
    <section>
      <div class="document-title center">${title}${number ? ` N° ${escapeHtml(number)}` : ""}</div>
      ${payload.is_unpaid ? '<div class="document-title center">NON PAYÉE</div>' : ""}
      <div class="meta">
        ${payload.table_number ? `<div class="meta-row"><span class="meta-label">Table :</span><span class="meta-value">${escapeHtml(payload.table_number)}</span></div>` : ""}
        ${payload.area_name ? `<div class="meta-row"><span class="meta-label">Aire :</span><span class="meta-value">${escapeHtml(payload.area_name)}</span></div>` : ""}
        ${payload.cashier_name ? `<div class="meta-row"><span class="meta-label">Caissier :</span><span class="meta-value">${escapeHtml(payload.cashier_name)}</span></div>` : ""}
        ${payload.server_name ? `<div class="meta-row"><span class="meta-label">Serveur :</span><span class="meta-value">${escapeHtml(payload.server_name)}</span></div>` : ""}
        ${dateLabel ? `<div class="meta-row"><span class="meta-label">Date :</span><span class="meta-value">${escapeHtml(dateLabel)}</span></div>` : ""}
      </div>
    </section>
    <div class="rule"></div>
    <section>
      <div class="section-title">ARTICLES</div>
      ${items.map((item) => {
        const quantity = toMoney(item?.quantity ?? item?.qty) || 1;
        const name = item?.display_name || item?.name || item?.product_name || "Produit";
        return `<div class="item"><div class="item-main"><div class="item-name">${quantity}× ${escapeHtml(name)}</div><div class="money">${formatMoney(computeThermalLineTotal(item))}</div></div>${renderExtras(item?.extras)}</div>`;
      }).join("")}
    </section>
    <div class="rule"></div>
    <section class="totals">
      <div class="row"><span>Sous-total</span><span class="money">${formatMoney(payload.subtotal)}</span></div>
      ${discount > 0.01 ? `<div class="row"><span>Réduction</span><span class="money">-${formatMoney(discount)}</span></div>` : ""}
      <div class="row total"><span>Total</span><span class="money">${formatMoney(payload.total)}</span></div>
      ${isReceipt ? `
        <div class="section-title payment-title">MODE DE PAIEMENT</div>
        ${(methods.length ? methods : [{ method: "Espèces", amount: payload.amount_received || payload.total }]).map((method) => {
          const rawLabel = method?.method || method?.name || "Paiement";
          const label = isCreditMethod(rawLabel) ? "Reste à payer — crédit" : rawLabel;
          return `<div class="row"><span>${escapeHtml(label)}</span><span class="money">${formatMoney(method?.amount)}</span></div>`;
        }).join("")}
        ${payload.credit_customer_name ? `<div class="meta-row"><span class="meta-label">Client :</span><span class="meta-value">${escapeHtml(payload.credit_customer_name)}</span></div>` : ""}
        <div class="row"><span>Reçu</span><span class="money">${formatMoney(payload.amount_received ?? payload.total)}</span></div>
        <div class="row"><span>Rendu</span><span class="money">${formatMoney(payload.change_given)}</span></div>
      ` : ""}
    </section>
    ${isReceipt ? renderReserve(payload.reserve) : ""}
    <footer class="footer"><div>Merci pour votre visite !</div><div>Powered by MOO Sync - ${new Date().getFullYear()}</div></footer>
  </main>
</body>
</html>`;
};

export {
  computeThermalLineTotal,
  escapeHtml,
  normalizePaperWidth,
  renderReserve,
  renderUsbTicketHtml,
};
